import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Switch,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { post, get } from "../../../utils/http";
import {
  STATUS,
  STATUS_WORDS_MAP,
  STATUS_TAG_COLOR,
} from "../../../utils/consts";
import { PhraseData } from "../types";
import AudioPlayButton from "./AudioPlayButton";

interface PhraseTableProps {
  data: PhraseData[];
  sceneId: string;
}

function PhraseTable({ data: initialData, sceneId }: PhraseTableProps) {
  const [phraseData, setPhraseData] = useState<PhraseData[]>(initialData);
  const [phraseModalVisible, setPhraseModalVisible] = useState(false);
  const [phraseForm] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState<PhraseData | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<PhraseData | null>(null);
  const [generatingAudioId, setGeneratingAudioId] = useState<string | null>(
    null,
  );

  const fetchPhraseData = async () => {
    try {
      const response = await get("/api/manage/scene-phrases", { scene_id: sceneId });
      console.log("🚀 ~ fetchPhraseData ~ response:", response);
      if (response?.data?.code === 0) {
        setPhraseData(
          Array.isArray(response.data.data) ? response.data.data : [],
        );
      }
    } catch (error) {
      console.error("🚀 ~ fetchPhraseData ~ error:", error);
    }
  };

  useEffect(() => {
    setPhraseData(initialData);
  }, [initialData]);

  const phraseColumns: ColumnsType<PhraseData> = [
    {
      title: "短句",
      dataIndex: "phrase_text",
      key: "phrase_text",
    },
    {
      title: "翻译",
      dataIndex: "phrase_translation",
      key: "phrase_translation",
    },
    {
      title: "例句",
      dataIndex: "example_sentence",
      key: "example_sentence",
      render: (example_sentence, record) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span>{example_sentence || "暂无例句"}</span>
          <Button
            type="link"
            size="small"
            onClick={async () => {
              try {
                const response = await post("/api/manage/generate-example-sentence", {
                  phrase_text: record.phrase_text,
                  phrase_id: record.id,
                });
                if (response?.data?.code === 0) {
                  message.success("例句生成成功");
                  fetchPhraseData();
                } else {
                  message.error("例句生成失败");
                }
              } catch (error) {
                console.error("例句生成失败:", error);
                message.error("例句生成失败");
              }
            }}
          >
            AI生成
          </Button>
        </div>
      ),
    },
    {
      title: "音频",
      dataIndex: "voice",
      key: "voice",
      render: (voice, record) =>
        voice ? (
          <AudioPlayButton voiceUrl={voice} recordId={record.id} />
        ) : (
          "暂无音频"
        ),
    },
    {
      title: "是否启用",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch
            checked={status === STATUS.INUSE}
            onChange={(checked) => {
              const newStatus = checked ? STATUS.INUSE : STATUS.DISABLE;
              post("/api/manage/phrase-update", {
                id: record.id,
                status: newStatus,
              })
                .then((res) => {
                  if (res?.data?.code === 0) {
                    message.success("状态更新成功");
                    fetchPhraseData();
                  } else {
                    message.error("状态更新失败");
                  }
                })
                .catch((error) => {
                  console.error("状态更新失败:", error);
                  message.error("状态更新失败");
                });
            }}
          />
          <Tag color={STATUS_TAG_COLOR[status as STATUS]}>
            {STATUS_WORDS_MAP[status as STATUS]}
          </Tag>
        </span>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 250,
      render: (_, record) => (
        <span>
          <a style={{ marginRight: 16 }} onClick={() => handleEdit(record)}>
            编辑
          </a>
          <Button
            type="link"
            loading={generatingAudioId === record.id}
            disabled={generatingAudioId !== null}
            onClick={() => handleGenerateAudio(record)}
            style={{ marginRight: 16 }}
          >
            生成音频
          </Button>
          <a onClick={() => handleDelete(record)}>删除</a>
        </span>
      ),
    },
  ];

  const handleAddPhrase = () => {
    setPhraseModalVisible(true);
  };

  const handlePhraseModalOk = async () => {
    try {
      const values = await phraseForm.validateFields();
      const apiUrl = editingRecord ? "/api/manage/phrase-update" : "/api/manage/phrase-add";
      const requestData = editingRecord
        ? {
            id: editingRecord.id,
            phrase_text: values.content,
            phrase_translation: values.translation,
            example_sentence: values.example_sentence,
            status: values.status,
          }
        : {
            scene_id: sceneId,
            phrase_text: values.content,
            phrase_translation: values.translation,
            example_sentence: values.example_sentence,
            status: values.status,
          };
      const response = await post(apiUrl, requestData);
      console.log("🚀 ~ handlePhraseModalOk ~ response:", response);
      if (response?.data?.code === 0) {
        setPhraseModalVisible(false);
        phraseForm.resetFields();
        setEditingRecord(null);
        fetchPhraseData();
      }
    } catch (error) {
      console.error("🚀 ~ handlePhraseModalOk ~ error:", error);
    }
  };

  const handlePhraseModalCancel = () => {
    setPhraseModalVisible(false);
    phraseForm.resetFields();
    setEditingRecord(null);
  };

  const handleEdit = (record: PhraseData) => {
    setEditingRecord(record);
    phraseForm.setFieldsValue({
      content: record.phrase_text,
      translation: record.phrase_translation,
      example_sentence: record.example_sentence,
      status: record.status,
    });
    setPhraseModalVisible(true);
  };

  const handleDelete = (record: PhraseData) => {
    setDeletingRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;
    try {
      const response = await post("/api/manage/phrase-delete", {
        id: deletingRecord.id,
      });
      console.log("🚀 ~ handleDeleteConfirm ~ response:", response);
      if (response?.data?.code === 0) {
        setDeleteModalVisible(false);
        setDeletingRecord(null);
        fetchPhraseData();
      }
    } catch (error) {
      console.error("🚀 ~ handleDeleteConfirm ~ error:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingRecord(null);
  };

  const handleGenerateAudio = async (record: PhraseData) => {
    if (generatingAudioId !== null) {
      return;
    }

    setGeneratingAudioId(record.id);

    try {
      const response = await post("/api/manage/text-to-audio-and-save", {
        text: record.phrase_text,
        table_type: "phrase",
        record_id: record.id,
      });
      console.log("🚀 ~ handleGenerateAudio ~ response:", response);
      if (response?.data?.code === 0) {
        message.success("音频生成成功");
        fetchPhraseData();
      }
    } catch (error) {
      console.error("🚀 ~ handleGenerateAudio ~ error:", error);
      message.error("音频生成失败");
    } finally {
      setGeneratingAudioId(null);
    }
  };

  return (
    <>
      <Table
        columns={phraseColumns}
        dataSource={phraseData}
        rowKey="id"
        pagination={false}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={handleAddPhrase}
                >
                  添加短语
                </Button>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <Modal
        title={editingRecord ? "编辑短语" : "添加短语"}
        open={phraseModalVisible}
        onOk={handlePhraseModalOk}
        onCancel={handlePhraseModalCancel}
        width={600}
      >
        <Form form={phraseForm} layout="vertical">
          <Form.Item
            label="短句"
            name="content"
            rules={[{ required: true, message: "请输入短句" }]}
          >
            <Input placeholder="请输入短句" />
          </Form.Item>
          <Form.Item
            label="翻译"
            name="translation"
            rules={[{ required: true, message: "请输入翻译" }]}
          >
            <Input placeholder="请输入翻译" />
          </Form.Item>
          <Form.Item label="例句" name="example_sentence">
            <Input placeholder="请输入例句" />
          </Form.Item>
          <Form.Item
            label="是否启用"
            name="status"
            rules={[{ required: true, message: "请选择是否启用" }]}
          >
            <Select placeholder="请选择是否启用">
              <Select.Option value={STATUS.INUSE}>
                {STATUS_WORDS_MAP[STATUS.INUSE]}
              </Select.Option>
              <Select.Option value={STATUS.DISABLE}>
                {STATUS_WORDS_MAP[STATUS.DISABLE]}
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="确认"
        cancelText="取消"
      >
        <p>确定要删除这个短语吗？</p>
      </Modal>
    </>
  );
}

export default PhraseTable;
